import type { MaybeRefOrGetter } from 'vue';
import { shallowRef, toValue, watchEffect } from 'vue';

/**
 * Preserves a stable BlockRender prop bag while shallow contents stay equal.
 *
 * Field display composables often rebuild equivalent objects on parent
 * re-renders. Keeping the previous object identity avoids unnecessary block
 * patching in dense UIs like virtualized tables.
 */
export function useStableRenderBlockProps(
	getProps: MaybeRefOrGetter<Record<string, unknown>>,
) {
	const blockProps = shallowRef<Record<string, unknown>>(toValue(getProps));

	watchEffect(() => {
		const nextProps = toValue(getProps);

		if (!haveSameShallowEntries(blockProps.value, nextProps)) {
			blockProps.value = nextProps;
		}
	});

	return blockProps;
}

/**
 * Returns true when both records expose the same keys and each value matches
 * by `Object.is`.
 */
function haveSameShallowEntries(
	left: Record<string, unknown>,
	right: Record<string, unknown>,
) {
	const leftKeys = Object.keys(left);
	const rightKeys = Object.keys(right);

	if (leftKeys.length !== rightKeys.length) {
		return false;
	}

	for (const key of leftKeys) {
		if (!(key in right) || !Object.is(left[key], right[key])) {
			return false;
		}
	}

	return true;
}
